import React, { useState, useEffect } from 'react';
import type { ReactElement, MutableRefObject } from 'react';
import { Modal, typographyHelper } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import { LocationSearchButton } from './LocationSearchButton';
import type {
  ExtendedLocationSuggestOption,
  ExtendedMultiLocationSuggestOption,
  SelectedFiltersOptionsType,
} from '../model/constants/filterTypes';
import useMedia from '../useMedia';
import { SmallScreenLocationSuggestDialog } from './SmallScreenLocationSuggestDialog';
import { LargeScreenLocationSuggestPopup } from './LargeScreenLocationSuggestPopup';
import { buildLocationSearchBtnLabel } from '../locationSearch';
import SingleLocationSuggest from './SingleLocationSuggest';
import { getCustomerRecentLocations } from '../customerRecentSearches';

export type LocationSearchBarProps = {
  selectedFiltersOptions: SelectedFiltersOptionsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  onLocationSearchBoxClick: () => void;
  isFromHomePage?: boolean;
  customerLocationSuggestRef?: MutableRefObject<HTMLInputElement | null>;
  isForSingleLocation?: boolean;
};

const StyledOverlay = styled.div<{
  isFromHomePage: boolean;
}>`
  position: absolute;
  z-index: 90;
  height: 3.75rem;
  box-shadow: 0px 1px 16px rgba(0, 0, 0, 0.16);
  border-radius: 0.5rem;
  ${({ isFromHomePage }) =>
    (isFromHomePage
      ? css`
          width: 30.75rem;
          left: 30.5%;
        `
      : css`
          width: 100%;
          top: -7px;
          left: 0;
        `)};
  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    width: 30.75rem;

    .ListboxWrapper {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
      border-radius: 0 0 ${({ theme }) => theme.border.radius.medium}
        ${({ theme }) => theme.border.radius.medium};
    }
    ${({ isFromHomePage }) =>
    (isFromHomePage
      ? css`
            width: 29.75rem;
            left: 32.5%;
            ${typographyHelper('body01')};
            box-shadow: none;
          `
      : css`
            width: 30rem;
          `)};
  }
`;

const StyledContainer = styled.div`
  width: 100%;
`;

export const LocationSearchBar = ({
  selectedFiltersOptions,
  initialFilterOptions,
  setSelectedFiltersOptions,
  handleSearchOnClick,
  onLocationSearchBoxClick,
  isFromHomePage = false,
  customerLocationSuggestRef,
  isForSingleLocation = false,
}: LocationSearchBarProps): ReactElement => {
  const [isOpenLocationSuggestPopup, setIsOpenLocationSuggestPopup] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const { isMediumScreen, isLargeScreen } = useMedia();
  const isSmallScreen = !isMediumScreen && !isLargeScreen;
  const { boundingBox, searchLocations: selectedLocations } = selectedFiltersOptions;
  const [searchLocations, setSearchLocations] = useState(selectedLocations);

  const showPopup = (shouldShowPopup: boolean) => {
    setIsOpenLocationSuggestPopup(shouldShowPopup);
    setShowOverlay?.(shouldShowPopup);
  };

  const openPopup = () => {
    showPopup(true);
    onLocationSearchBoxClick();
  };

  const closePopup = () => showPopup(false);

  useEffect(() => {
    if (!showOverlay && isOpenLocationSuggestPopup) {
      setIsOpenLocationSuggestPopup(false);
    }
  }, [showOverlay]);

  const locationsToDisplayText = (
    locations: Array<ExtendedMultiLocationSuggestOption | ExtendedLocationSuggestOption>,
  ): string => locations.map((location) => location.display.text).join();

  const hasSearchLocationChanged = (
    initial: Array<ExtendedMultiLocationSuggestOption | ExtendedLocationSuggestOption>,
    current: Array<ExtendedMultiLocationSuggestOption | ExtendedLocationSuggestOption>,
  ): boolean => locationsToDisplayText(initial) !== locationsToDisplayText(current);

  useEffect(() => {
    if (
      initialFilterOptions.searchLocations &&
      searchLocations &&
      hasSearchLocationChanged(initialFilterOptions.searchLocations, searchLocations)
    ) {
      setSearchLocations(initialFilterOptions.searchLocations);
      setSelectedFiltersOptions({
        ...selectedFiltersOptions,
        searchLocations: initialFilterOptions.searchLocations,
      });
    }
  }, [initialFilterOptions]);

  return !isForSingleLocation ? (
    <>
      <LocationSearchButton isFromHomePage={isFromHomePage} onClick={openPopup}>
        {buildLocationSearchBtnLabel(searchLocations, boundingBox)}
      </LocationSearchButton>

      {isSmallScreen ? (
        <Modal
          opened={isOpenLocationSuggestPopup}
          onRequestClose={() => setIsOpenLocationSuggestPopup(false)}
          getAppElement={() => document.getElementById('wrapper') ?? ''}
        >
          <SmallScreenLocationSuggestDialog
            onClose={() => {
              setIsOpenLocationSuggestPopup(false);
            }}
            selectedFiltersOptions={selectedFiltersOptions}
            initialFilterOptions={initialFilterOptions}
            setSelectedFiltersOptions={setSelectedFiltersOptions}
            handleSearchOnClick={handleSearchOnClick}
          />
        </Modal>
      ) : (
        isOpenLocationSuggestPopup && (
          <StyledOverlay isFromHomePage={isFromHomePage}>
            <LargeScreenLocationSuggestPopup
              closePopup={closePopup}
              selectedFiltersOptions={selectedFiltersOptions}
              initialFilterOptions={initialFilterOptions}
              setSelectedFiltersOptions={setSelectedFiltersOptions}
              handleSearchOnClick={handleSearchOnClick}
              isFromHomePage={isFromHomePage}
              showDoneButton={!isFromHomePage}
            />
          </StyledOverlay>
        )
      )}
    </>
  ) : (
    <StyledContainer>
      <SingleLocationSuggest
        selectedFiltersOptions={selectedFiltersOptions}
        setSelectedFiltersOptions={setSelectedFiltersOptions}
        singleLocationSuggestRef={customerLocationSuggestRef}
        recentLocations={getCustomerRecentLocations()}
        handleOnClick={onLocationSearchBoxClick}
      />
    </StyledContainer>
  );
};
